import SEO from '@/components/SEO';

export default function AdoptionProcess() {
  const steps = [
    {
      number: '01',
      title: 'Browse Available Kittens',
      description:
        'Visit our Available Kittens page to see our current kittens. Each listing includes photos, personality descriptions, and health information.',
    },
    {
      number: '02',
      title: 'Contact Us',
      description:
        'Once you find a kitten you\'re interested in, reach out to us via phone (929-333-1936) or our contact form. We\'ll answer any questions you have and provide additional information.',
    },
    {
      number: '03',
      title: 'Reserve Your Kitten',
      description:
        'No deposit is required to reserve your kitten. Simply let us know which kitten you\'re interested in, and we\'ll confirm availability. Payment is made only when you pick up your kitten.',
    },
    {
      number: '04',
      title: 'Prepare for Your Kitten',
      description:
        'While waiting for your kitten to be ready, we\'ll provide you with a care guide and recommendations for supplies. We\'re always available to answer questions.',
    },
    {
      number: '05',
      title: 'Pickup & Payment',
      description:
        'When your kitten is at least 12 weeks old and fully weaned, you can schedule a pickup. Full payment is made at pickup. We also offer FREE delivery to your location! You\'ll receive all health records and documentation.',
    },
    {
      number: '06',
      title: 'Lifetime Support',
      description:
        'Our relationship doesn\'t end at adoption. We\'re here to support you throughout your cat\'s life with advice, guidance, and care tips.',
    },
  ];

  return (
    <main className="py-20 min-h-screen">
      <SEO 
        title="Adoption Process | How to Adopt a Kitten from KittenState"
        description="Learn how to adopt a Scottish Fold or Scottish Straight kitten from KittenState. Simple 6-step process with no deposits required - pay only when you pick up your kitten."
        keywords="kitten adoption process, how to adopt Scottish Fold, adopt Scottish Straight, KittenState adoption, no deposit kitten"
        url="https://kittenstate.com/adoption-process"
      />
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-serif font-semibold tracking-tight leading-tight text-stone-800 mb-6 text-center">
            Adoption Process
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-stone-600 text-center mb-12 max-w-2xl mx-auto">
            We want to make the adoption process as smooth and transparent as possible. Here's what
            to expect when you adopt a kitten from KittenState.
          </p>

          <div className="space-y-8 mb-16">
            {steps.map((step) => (
              <div
                key={step.number}
                data-testid={`adoption-step-${step.number}`}
                className="bg-white p-8 rounded-2xl border border-stone-100 transition-all hover:border-[#E85D75]/30"
              >
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-[#E85D75]/10 flex items-center justify-center">
                      <span className="text-2xl font-serif font-semibold text-[#E85D75]">
                        {step.number}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl md:text-3xl font-serif font-medium text-stone-800 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-base md:text-lg leading-relaxed text-stone-600">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-stone-50 p-8 rounded-2xl border border-stone-100">
            <h2 className="text-4xl md:text-5xl font-serif font-medium text-stone-800 mb-6">
              Responsible Placement
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-stone-600 mb-4">
              At KittenState, we are committed to responsible breeding and placement. We take the
              time to get to know each potential adopter to ensure our kittens are going to loving,
              stable homes.
            </p>
            <p className="text-base md:text-lg leading-relaxed text-stone-600 mb-4">
              We ask that adopters:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start text-stone-600">
                <span className="inline-block w-2 h-2 bg-[#E85D75] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Be prepared to provide a safe, indoor environment</span>
              </li>
              <li className="flex items-start text-stone-600">
                <span className="inline-block w-2 h-2 bg-[#E85D75] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Commit to regular veterinary care</span>
              </li>
              <li className="flex items-start text-stone-600">
                <span className="inline-block w-2 h-2 bg-[#E85D75] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Provide proper nutrition and enrichment</span>
              </li>
              <li className="flex items-start text-stone-600">
                <span className="inline-block w-2 h-2 bg-[#E85D75] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Keep us updated on your kitten's progress</span>
              </li>
            </ul>
          </div>

          <div className="mt-12 text-center">
            <img
              src="https://images.unsplash.com/photo-1691351943492-cfee023e9cbf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxTY290dGlzaCUyMFN0cmFpZ2h0JTIwa2l0dGVuJTIwcGxheWluZ3xlbnwwfHx8fDE3NzI3MTM3Nzh8MA&ixlib=rb-4.1.0&q=85"
              alt="Playful kitten"
              className="w-full h-96 object-cover rounded-2xl mb-6"
            />
            <p className="text-base md:text-lg leading-relaxed text-stone-600 mb-6">
              Ready to start your adoption journey?
            </p>
            <a href="/contact">
              <button className="bg-[#E85D75] text-white hover:bg-[#D64560] shadow-lg shadow-[#E85D75]/20 rounded-full px-8 py-6 text-lg transition-all hover:scale-105 active:scale-95">
                Contact Us Today
              </button>
            </a>
          </div>
        </div>
      </div>
    </main>
  );
}
